import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Diamond, KeyRound, Copy, RefreshCw, Trash2, Eye, EyeOff, BookOpen, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";

export default function AdminResellerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [recharges, setRecharges] = useState([]);
  const [allocStart, setAllocStart] = useState("");
  const [allocEnd, setAllocEnd] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [keyBusy, setKeyBusy] = useState(false);
  const [rcStart, setRcStart] = useState("");
  const [rcEnd, setRcEnd] = useState("");

  useEffect(() => {
    const d = new Date(Date.now() - 30 * 24 * 3600 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    setRcStart(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00`);
  }, []);

  const refresh = async () => {
    const [d, a, r] = await Promise.all([
      api.get(`/admin/resellers/${id}`),
      api.get(`/admin/allocations`, { params: { reseller_id: id } }),
      api.get(`/admin/recharges`, { params: { reseller_id: id } }),
    ]);
    setData(d.data);
    setAllocations(a.data);
    setRecharges(r.data);
  };

  useEffect(() => { refresh(); }, [id]);

  const handleStatus = async (status) => {
    try {
      await api.patch(`/admin/resellers/${id}`, { status });
      toast.success(`Reseller ${status}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const generateKey = async (regen) => {
    if (regen && !window.confirm("Regenerate the API key? The old key will stop working immediately.")) return;
    setKeyBusy(true);
    try {
      const { data: d } = await api.post(`/admin/resellers/${id}/api-key`);
      toast.success(regen ? "API key regenerated" : "API key generated");
      setShowKey(true);
      setData((prev) => ({ ...prev, reseller: { ...prev.reseller, api_key: d.api_key } }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setKeyBusy(false);
    }
  };

  const revokeKey = async () => {
    if (!window.confirm("Revoke this API key? The reseller will lose programmatic access.")) return;
    setKeyBusy(true);
    try {
      await api.delete(`/admin/resellers/${id}/api-key`);
      toast.success("API key revoked");
      setData((prev) => ({ ...prev, reseller: { ...prev.reseller, api_key: "" } }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setKeyBusy(false);
    }
  };

  const copyKey = async () => {
    const text = data.reseller.api_key;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("API key copied");
  };

  if (!data) return <div className="text-zinc-500">Loading…</div>;
  const r = data.reseller;
  const successDiamonds = recharges
    .filter((x) => x.status === "success")
    .reduce((s, x) => s + (x.diamonds || 0), 0);

  const filteredAllocations = allocations.filter((a) => {
    if (!allocStart && !allocEnd) return true;
    const d = new Date(a.created_at);
    if (allocStart && d < new Date(`${allocStart}T00:00:00`)) return false;
    if (allocEnd && d > new Date(`${allocEnd}T23:59:59`)) return false;
    return true;
  });
  const allocNet = filteredAllocations.reduce((s, a) => s + (a.amount || 0), 0);

  const filteredRecharges = recharges.filter((rr) => {
    const t = new Date(rr.created_at).getTime();
    if (rcStart && t < new Date(rcStart).getTime()) return false;
    if (rcEnd && t > new Date(rcEnd).getTime()) return false;
    return true;
  });

  const exportRecharges = () => {
    const headers = ["Timestamp", "Platform", "Type", "Tier", "Mode", "Recipient UID", "Diamonds", "Balance after", "Status", "Note"];
    const rows = filteredRecharges.map((rr) => [
      new Date(rr.created_at).toLocaleString(),
      rr.platform_name || "Trumeet",
      rr.type === "noble" ? "Noble" : "Transfer",
      rr.tier_label || "",
      rr.mode || "",
      rr.recipient_uid || "",
      rr.diamonds ?? 0,
      rr.balance_after ?? "",
      rr.status || "",
      rr.note || "",
    ]);
    const who = (r.email || "reseller").split("@")[0];
    downloadCsv(`recharges_${who}_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  return (
    <div className="space-y-6" data-testid="reseller-detail-page">
      <Link to="/admin/resellers" className="inline-flex items-center text-sm text-zinc-600 hover:text-zinc-900">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to resellers
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Profile</p>
          <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">{r.name || "—"}</h1>
          <p className="text-zinc-500 mt-1">
            {r.email} · {r.phone || "no phone"}
            {(r.city || r.state) && ` · ${[r.city, r.state].filter(Boolean).join(", ")}`}
          </p>
        </div>
        <div className="flex gap-2">
          {r.status === "active" ? (
            <Button variant="outline" onClick={() => handleStatus("disabled")} className="text-red-600 hover:text-red-700">Disable</Button>
          ) : (
            <Button onClick={() => handleStatus("active")} className="bg-green-600 hover:bg-green-700">Enable</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard label="Total recharges" value={recharges.length.toLocaleString()} />
        <SummaryCard label="Successful sales" value={recharges.filter((x) => x.status === "success").length.toLocaleString()} />
        <SummaryCard label="Diamonds sold" value={successDiamonds.toLocaleString()} />
      </div>

      <div className="space-y-3">
        {Object.entries(r.balances || {}).map(([key, b]) => (
          <div key={key} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`balance-card-${key}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-zinc-900">{b.name || key}</div>
              <Diamond className="w-4 h-4 text-zinc-400" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Stat label="Allocated" value={(b.allocated ?? 0).toLocaleString()} />
              <Stat label="Used" value={(b.used ?? 0).toLocaleString()} />
              <Stat label="Balance" value={(b.balance ?? 0).toLocaleString()} />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="api-key-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-zinc-900 text-white flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-display text-base font-medium text-zinc-900">API Key</h3>
              <p className="text-xs text-zinc-500 mt-0.5 max-w-md">
                Lets this reseller push recharges programmatically. The API respects the same balances,
                test-mode and limits as the portal.{" "}
                <Link to="/admin/api-docs" className="inline-flex items-center gap-1 text-zinc-900 underline underline-offset-2" data-testid="api-docs-link">
                  <BookOpen className="w-3 h-3" /> View API docs
                </Link>
              </p>
            </div>
          </div>
          {r.api_key ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => generateKey(true)} disabled={keyBusy} data-testid="regenerate-api-key-btn">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Regenerate
              </Button>
              <Button variant="outline" onClick={revokeKey} disabled={keyBusy} className="text-red-600 hover:text-red-700" data-testid="revoke-api-key-btn">
                <Trash2 className="w-4 h-4 mr-1.5" /> Revoke
              </Button>
            </div>
          ) : (
            <Button onClick={() => generateKey(false)} disabled={keyBusy} className="bg-zinc-900 hover:bg-zinc-800" data-testid="generate-api-key-btn">
              <KeyRound className="w-4 h-4 mr-1.5" /> Generate key
            </Button>
          )}
        </div>
        {r.api_key ? (
          <div className="mt-4 flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
            <code className="flex-1 font-mono text-sm text-zinc-800 break-all" data-testid="api-key-value">
              {showKey ? r.api_key : "•".repeat(Math.min(r.api_key.length, 44))}
            </code>
            <button onClick={() => setShowKey((s) => !s)} className="p-2 rounded-md hover:bg-zinc-200 text-zinc-600" data-testid="toggle-api-key-btn" title={showKey ? "Hide" : "Show"}>
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button onClick={copyKey} className="p-2 rounded-md hover:bg-zinc-200 text-zinc-600" data-testid="copy-api-key-btn" title="Copy">
              <Copy className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="mt-4 text-sm text-zinc-400" data-testid="api-key-empty">No API key. Generate one to enable API access for this reseller.</div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="allocations-section">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <h3 className="font-display text-base font-medium text-zinc-900">Allocations</h3>
              <p className="text-xs text-zinc-500 mt-0.5" data-testid="allocations-net-total">
                Net allotted{(allocStart || allocEnd) ? " (filtered)" : ""}:{" "}
                <span className={`font-mono font-semibold ${allocNet < 0 ? "text-amber-700" : "text-zinc-900"}`}>
                  {allocNet < 0 ? "−" : "+"}{Math.abs(allocNet).toLocaleString()}
                </span>{" "}diamonds · {filteredAllocations.length} record{filteredAllocations.length === 1 ? "" : "s"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="allocations-date-filter">
              <input
                type="date"
                value={allocStart}
                max={allocEnd || undefined}
                onChange={(e) => setAllocStart(e.target.value)}
                data-testid="allocations-start-date"
                className="h-8 px-2 text-xs rounded-md border border-zinc-300 bg-white"
              />
              <span className="text-xs text-zinc-400">→</span>
              <input
                type="date"
                value={allocEnd}
                min={allocStart || undefined}
                onChange={(e) => setAllocEnd(e.target.value)}
                data-testid="allocations-end-date"
                className="h-8 px-2 text-xs rounded-md border border-zinc-300 bg-white"
              />
              {(allocStart || allocEnd) && (
                <button
                  onClick={() => { setAllocStart(""); setAllocEnd(""); }}
                  data-testid="allocations-clear-dates"
                  className="px-2 py-1 rounded text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          {filteredAllocations.length === 0 ? (
            <div className="text-sm text-zinc-400" data-testid="allocations-empty">
              {allocations.length === 0 ? "No allocations yet" : "No allocations in selected date range"}
            </div>
          ) : (
            <div>
          {filteredAllocations.map((a) => (
            <div key={a.id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-b-0 text-sm">
              <div>
                <div className={`font-medium ${a.amount < 0 ? "text-amber-700" : "text-zinc-900"}`}>
                  {a.amount < 0 ? "−" : "+"}{Math.abs(a.amount).toLocaleString()} diamonds
                  <span className="text-xs text-zinc-500"> · {a.platform_name || "Trumeet"}{a.amount < 0 ? " · taken back" : ""}</span>
                </div>
                <div className="text-zinc-500 text-xs">{a.note || "—"} · {new Date(a.created_at).toLocaleString()}</div>
              </div>
              <div className="text-xs text-zinc-500 font-mono">{a.admin_email}</div>
            </div>
          ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="reseller-recharges-section">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <h3 className="font-display text-base font-medium text-zinc-900">Recharges</h3>
              <p className="text-xs text-zinc-500 mt-0.5" data-testid="reseller-recharges-count">
                {filteredRecharges.length} record{filteredRecharges.length === 1 ? "" : "s"}{(rcStart || rcEnd) ? " (filtered)" : ""}
              </p>
            </div>
            <button
              onClick={exportRecharges}
              disabled={!filteredRecharges.length}
              data-testid="export-reseller-recharges-csv"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-40"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="reseller-recharges-date-filter">
            <span className="text-xs text-zinc-400">From</span>
            <input type="datetime-local" value={rcStart} max={rcEnd || undefined} onChange={(e) => setRcStart(e.target.value)}
              data-testid="reseller-recharges-start" className="h-8 px-2 text-xs rounded-md border border-zinc-300 bg-white" />
            <span className="text-xs text-zinc-400">To</span>
            <input type="datetime-local" value={rcEnd} min={rcStart || undefined} onChange={(e) => setRcEnd(e.target.value)}
              data-testid="reseller-recharges-end" className="h-8 px-2 text-xs rounded-md border border-zinc-300 bg-white" />
            {(rcStart || rcEnd) && (
              <button onClick={() => { setRcStart(""); setRcEnd(""); }} data-testid="reseller-recharges-clear"
                className="px-2 py-1 rounded text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100">Clear</button>
            )}
          </div>
          {filteredRecharges.length === 0 ? (
            <div className="text-sm text-zinc-400" data-testid="reseller-recharges-empty">
              {recharges.length === 0 ? "No recharges yet" : "No recharges in selected range"}
            </div>
          ) : (
            <div className="max-h-[420px] overflow-y-auto">
              {filteredRecharges.slice(0, 1000).map((rr) => (
                <div key={rr.id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-b-0 text-sm">
                  <div>
                    <div className="text-zinc-900 font-medium font-mono">{rr.recipient_uid} <span className="text-xs text-zinc-500 font-sans">· {rr.platform_name || "Trumeet"}</span></div>
                    <div className="text-zinc-500 text-xs">{new Date(rr.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-zinc-900 tabular-nums font-mono">{rr.diamonds}</div>
                      <div className="text-[10px] text-zinc-400 font-mono">bal: {rr.balance_after == null ? "—" : rr.balance_after.toLocaleString()}</div>
                    </div>
                    <StatusPill status={rr.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="font-display text-3xl font-semibold tabular-nums font-mono mt-1 text-zinc-900">{value}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="font-display text-2xl font-semibold tabular-nums font-mono">{value}</div>
    </div>
  );
}

function Section({ title, empty, children }) {
  const hasChildren = Array.isArray(children) ? children.length > 0 : !!children;
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6">
      <h3 className="font-display text-base font-medium text-zinc-900 mb-3">{title}</h3>
      {!hasChildren ? <div className="text-sm text-zinc-400">{empty}</div> : <div>{children}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    success: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-800",
    pending: "bg-amber-100 text-amber-800",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${map[status] || map.pending}`}>
      {status}
    </span>
  );
}
